import {
  Avatar,
  ChakraBox as Box,
  ChakraFlex as Flex,
  ChakraGrid as Grid,
  ChakraGridItem as GridItem,
  RechartCartesianGrid as CartesianGrid,
  RechartLine as Line,
  RechartLineChart as LineChart,
  RechartReferenceArea as ReferenceArea,
  RechartReferenceLine as ReferenceLine,
  RechartResponsiveContainer as ResponsiveContainer,
  RechartTooltip as RechartsTooltip,
  RechartXAxis as XAxis,
  RechartYAxis as YAxis,
  Text,
  cornerRadius,
  useColorTokens,
} from '@equip.health/ui';
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { DateTime } from 'luxon';

import AssessmentCardActionMessage, {
  CompleteTogetherBannerVariant,
} from './AssessmentCardActionMessage';
import AssessmentCardHeader from './AssessmentCardHeader';
import { AssessmentCardRiskLevelTag } from './AssessmentCardRiskLevelTag';
import { AssessmentChartCardLegend } from './AssessmentChartCardLegend';
import { AssessmentChartLabelToValueMap } from './AssessmentChartLabelToValueMap';
import AssessmentChartTooltip from './AssessmentChartTooltip';
import { AssessmentUnchartedKeys } from './AssessmentChartUnchartedKeys';
import { AssessmentDisplayStatus } from './AssessmentDisplayStatus';
import { AssessmentFrequency } from './AssessmentFrequency';

const idPrefix = 'assessment-chart-card';

export type TimepointZoneStatus =
  | 'complete'
  | 'missing'
  | 'in-progress'
  | 'not-yet-applicable';

export type ChartTimepointZone = {
  label: string;
  subLabel?: string;
  startEpoch: number;
  endEpoch: number;
  status: TimepointZoneStatus;
};

export type AssessmentChartHighlightVariant =
  | 'timepoint-complete'
  | 'timepoint-missing'
  | 'timepoint-in-progress'
  | 'timepoint-not-yet-applicable'
  | 'assessment-current'
  | 'assessment-next'
  | 'assessment-overdue';

export type AssessmentChartHighlightZone = {
  label: string;
  subLabel?: string;
  startEpoch: number;
  endEpoch: number;
  variant: AssessmentChartHighlightVariant;
  /**
   * Lower layers render first. Use this to make overlapping highlight windows
   * stack predictably without requiring callers to sort them.
   */
  layer?: number;
};

export type AssessmentChartTimelineMarkerVariant =
  | 'admit'
  | 'week16'
  | 'discharge';

export type AssessmentChartTimelineMarker = {
  label: string;
  subLabel?: string;
  epoch: number;
  variant?: AssessmentChartTimelineMarkerVariant;
};

export type { CompleteTogetherBannerVariant } from './AssessmentCardActionMessage';

export interface AssessmentChartCardProps {
  assessmentName: string;
  frequency: AssessmentFrequency;
  descriptionText: string;
  id: string;
  isAssessmentActive: boolean;
  isHistoryEnabled: boolean;
  // When true, suppresses "Create new" actions on the card header (kebab and
  // inline CTA). Resume, View, and Delete remain available.
  hideCreateActions?: boolean;
  onClickCreateNew: (source: 'title' | 'kebab') => void;
  primaryScoreKey?: string; // a key of AssessmentChartLabelToValueMap that indicates which is the 'global' score, if any
  chartData: AssessmentChartLabelToValueMap[]; // Data must be in order, oldest to most recent epochCompletedOrUpdatedDate
  onViewAssessmentClick: (assessmentResponseId: string) => void;
  onViewHistoryClick: () => void;
  onResumeClick: (assessmentResponseId: string) => void;
  onCompleteTogetherClick: (
    assessmentResponseId: string,
    bannerVariant: CompleteTogetherBannerVariant,
  ) => void;
  onDeleteClick: (assessmentResponseId: string) => void;
  /**
   * Fires once per chart-line hover session, when the user moves off the line
   * (key → null) or onto a different line (keyA → keyB). Receives the line key
   * just left and the dwell duration in milliseconds. (GX-3402)
   */
  onLineHoverEnd?: (lineName: string, dwellMs: number) => void;
  /**
   * Fires when the user clicks a chart data point. Receives the line key the
   * dot belongs to plus the response id, so the consumer can fire a unified
   * Chart Line Interaction event in addition to opening the view modal.
   * Default behavior calls `onViewAssessmentClick` directly. (GX-3402)
   */
  onLineClick?: (lineName: string, assessmentResponseId: string) => void;
  /**
   * Fires when the score tooltip closes (active true → false), with the total
   * time the tooltip was visible. (GX-3402)
   */
  onTooltipDwellEnd?: (dwellMs: number) => void;
  avatarTooltipWrapper?: (props: {
    children: React.ReactNode;
    providerExternalId: string;
  }) => ReactNode;
  /**
   * When true, renders horizontal-only solid gridlines using a theme token.
   * Default false preserves legacy dashed grid on both axes. (GX-3321)
   */
  solidHorizontalGrid?: boolean;
  /**
   * When true, forces X-axis ticks to the first day of each month spanning
   * the chart domain. Default false uses Recharts auto-ticks. (GX-3321)
   */
  monthlyXAxisTicks?: boolean;
  /**
   * Controls X-axis tick label format.
   *  - 'default' (legacy): 'LL/dd' (e.g. '03/15').
   *  - 'monthYear': 3-char month ('Jun'); year appended on January or the
   *    first visible tick (e.g. "Jan '25"). (GX-3321)
   */
  xAxisLabelFormat?: 'default' | 'monthYear';
  /**
   * When true, hovering a trendline or legend item dims the other series
   * and legend entries. No-op on single-series charts. (GX-3321)
   */
  hoverFocusDim?: boolean;
  /**
   * When true, each series renders a dashed line that bridges across
   * non-COMPLETE (INCOMPLETE / NEW) points in addition to the solid line
   * that only connects consecutive COMPLETE points. Drives off the existing
   * `status` field on chartData. (GX-3321)
   */
  dashedMissingBridge?: boolean;
  /**
   * When false, the Recharts tooltip cursor (full-height vertical line that
   * tracks the mouse while hovering data points) is suppressed. The tooltip
   * popover still renders. Default true preserves legacy behavior. (GX-3321)
   */
  showHoverVerticalLine?: boolean;
  /**
   * When true, numeric scores in the score row and tooltip are padded to two
   * decimal places (e.g. 1.57, 4.00). Use for mean-scored assessments whose
   * clinical convention is decimal output — EDE-Q, PARDI-AR-Q, CET. Integer-
   * sum assessments (PHQ-8, GAD-7, CIA, etc.) should leave this false so
   * scores render at stored precision (7, not 7.00). Default false. (GX-3321)
   */
  padScoresToTwoDecimals?: boolean;
  /**
   * When set, fixes the Y-axis upper bound. Pair with yAxisMin for
   * assessment-specific ranges. Default undefined uses Recharts auto-scale.
   */
  yAxisMax?: number;
  /**
   * When set with yAxisMax, fixes the Y-axis lower bound. Default remains 0
   * when an explicit max is supplied.
   */
  yAxisMin?: number;
  /**
   * Optional fixed X-axis domain. When set, the chart domain is exactly
   * these bounds — chart data, highlight zones, and timeline markers that
   * fall outside are hidden rather than stretching the axis. When
   * undefined, the domain is derived from the union of data + annotations.
   */
  xAxisDomain?: [number, number];
  chartHeight?: number;
  /**
   * When true, renders a 1px solid vertical line at `Date.now()` stroked
   * with primary.50, plus a matching "Today" entry in the shaded-legend
   * row. No on-chart label. (GX-3321)
   */
  showTodayMarker?: boolean;
  highlightZones?: AssessmentChartHighlightZone[];
  timelineMarkers?: AssessmentChartTimelineMarker[];
  /**
   * Optional swatch + label rows appended to the chart legend to describe
   * shaded assessment windows (e.g. current, next, overdue). Callers supply
   * the label and the variant to read colors from; swatch fill/stroke are
   * resolved from highlightVariantToStyle so the legend always matches the
   * rendered shading. Rendered inline with the series legend items and
   * exempt from hover-dim. (GX-3321)
   */
  shadingLegendEntries?: Array<{
    label: string;
    variant: AssessmentChartHighlightVariant;
  }>;
  /**
   * Shaded time windows to render behind assessment trend lines. These are
   * independent of chartData so missed windows can display even when there is
   * no assessment response in the range.
   *
   * @deprecated Use highlightZones for new graph annotations.
   */
  timepointZones?: ChartTimepointZone[];
}

const orderedLineColors = [
  'accent.periwinkle',
  'tertiary.orange.base',
  'dataset.violet.base',
  'tertiary.marigold.base',
  'background.button.blue.active',
  'primary.50',
  'dataset.plum.base',
];

const MONTH_TICK_SPACING_THRESHOLD_MONTHS = 12;

const getMonthlyTicks = (startEpoch: number, endEpoch: number): number[] => {
  const ticks: number[] = [];
  const start = DateTime.fromMillis(startEpoch).startOf('month');
  const end = DateTime.fromMillis(endEpoch);
  const spanMonths = end.diff(start, 'months').months;
  const stepMonths = spanMonths > MONTH_TICK_SPACING_THRESHOLD_MONTHS ? 2 : 1;
  let cursor = start;
  while (cursor.toMillis() <= end.toMillis()) {
    ticks.push(cursor.toMillis());
    cursor = cursor.plus({ months: stepMonths });
  }
  return ticks;
};

const highlightVariantToStyle: Record<
  AssessmentChartHighlightVariant,
  {
    fill: string;
    fillOpacity: number;
    labelSuffix?: string;
    stroke?: string;
    strokeDasharray?: string;
    textColor: string;
  }
> = {
  'assessment-current': {
    fill: '#FFEDBE',
    fillOpacity: 0.5,
    stroke: '#FFC52E',
    textColor: '#775808',
  },
  'assessment-next': {
    fill: '#F9F9FA',
    fillOpacity: 1,
    stroke: '#E6E8ED',
    textColor: '#585D69',
  },
  'assessment-overdue': {
    fill: '#FAE9E8',
    fillOpacity: 1,
    stroke: '#C61910',
    textColor: '#7F0A0A',
  },
  'timepoint-complete': {
    fill: '#16a34a',
    fillOpacity: 0.1,
    stroke: '#16a34a',
    textColor: '#15803d',
  },
  'timepoint-in-progress': {
    fill: '#d97706',
    fillOpacity: 0.1,
    labelSuffix: ' In progress',
    stroke: '#d97706',
    textColor: '#b45309',
  },
  'timepoint-missing': {
    fill: '#dc2626',
    fillOpacity: 0.1,
    labelSuffix: ' Missing',
    stroke: '#dc2626',
    textColor: '#b91c1c',
  },
  'timepoint-not-yet-applicable': {
    fill: '#9ca3af',
    fillOpacity: 0.1,
    stroke: '#9ca3af',
    strokeDasharray: '4 4',
    textColor: '#6b7280',
  },
};

type TimelineMarkerStyle = {
  stroke: string;
  strokeDasharray?: string;
  textColor: string;
};

const buildTimelineMarkerVariantToStyle = (
  strokeColor: string,
  textColor: string,
): Record<AssessmentChartTimelineMarkerVariant, TimelineMarkerStyle> => {
  const shared: TimelineMarkerStyle = {
    stroke: strokeColor,
    strokeDasharray: '2 4',
    textColor,
  };
  return {
    admit: shared,
    discharge: shared,
    week16: shared,
  };
};

type HighlightZoneLabelProps = {
  label: string;
  subLabel?: string;
  textColor: string;
  viewBox?: {
    x?: number;
    y?: number;
    width?: number;
  };
};

const HighlightZoneLabel = ({
  label,
  subLabel,
  textColor,
  viewBox,
}: HighlightZoneLabelProps) => {
  if (
    viewBox?.x === undefined ||
    viewBox.y === undefined ||
    viewBox.width === undefined
  ) {
    return null;
  }

  const centerX = viewBox.x + viewBox.width / 2;
  const topY = viewBox.y + 8;

  return (
    <g transform={`translate(${centerX}, ${topY})`}>
      <circle cx="0" cy="0" fill="#fff" r="3" stroke={textColor} />
      <text
        fill={textColor}
        fontSize={11}
        fontWeight={600}
        textAnchor="middle"
        y={13}
      >
        {label}
      </text>
      {subLabel && (
        <text
          fill={textColor}
          fontSize={10}
          fontWeight={400}
          opacity={0.78}
          textAnchor="middle"
          y={26}
        >
          {subLabel}
        </text>
      )}
    </g>
  );
};

type TimelineMarkerLabelProps = {
  label: string;
  strokeColor: string;
  textColor: string;
  viewBox?: {
    x?: number;
    y?: number;
  };
};

const TIMELINE_PILL_FONT_SIZE = 10;
const TIMELINE_PILL_PADDING_X = 8;
const TIMELINE_PILL_HEIGHT = 24;
const TIMELINE_PILL_BORDER_RADIUS = 6;
const TIMELINE_PILL_CHAR_WIDTH = TIMELINE_PILL_FONT_SIZE * 0.6;
const TIMELINE_PILL_STEM_HEIGHT = 16;

const TimelineMarkerLabel = ({
  fillColor,
  label,
  strokeColor,
  strokeDasharray,
  textColor,
  viewBox,
}: TimelineMarkerLabelProps & {
  fillColor: string;
  strokeDasharray?: string;
}) => {
  if (viewBox?.x === undefined || viewBox.y === undefined) {
    return null;
  }

  const textWidth = label.length * TIMELINE_PILL_CHAR_WIDTH;
  const pillWidth = textWidth + TIMELINE_PILL_PADDING_X * 2;
  const stemTopY = viewBox.y - TIMELINE_PILL_STEM_HEIGHT;
  const pillTopY = stemTopY - TIMELINE_PILL_HEIGHT;

  return (
    <g>
      <line
        stroke={strokeColor}
        strokeDasharray={strokeDasharray ?? '2 4'}
        strokeLinecap="round"
        strokeWidth={1.5}
        x1={viewBox.x}
        x2={viewBox.x}
        y1={stemTopY}
        y2={viewBox.y}
      />
      <g transform={`translate(${viewBox.x}, ${pillTopY})`}>
        <rect
          fill={fillColor}
          height={TIMELINE_PILL_HEIGHT}
          rx={TIMELINE_PILL_BORDER_RADIUS}
          ry={TIMELINE_PILL_BORDER_RADIUS}
          stroke={strokeColor}
          strokeWidth={1}
          width={pillWidth}
          x={-pillWidth / 2}
          y={0}
        />
        <text
          fill={textColor}
          fontSize={TIMELINE_PILL_FONT_SIZE}
          fontWeight={500}
          textAnchor="middle"
          y={TIMELINE_PILL_HEIGHT / 2 + TIMELINE_PILL_FONT_SIZE / 2 - 1}
        >
          {label}
        </text>
      </g>
    </g>
  );
};

const mapTimepointZoneToHighlightZone = (
  zone: ChartTimepointZone,
): AssessmentChartHighlightZone => ({
  endEpoch: zone.endEpoch,
  label: zone.label,
  layer: 1,
  startEpoch: zone.startEpoch,
  subLabel: zone.subLabel,
  variant: `timepoint-${zone.status}` as AssessmentChartHighlightVariant,
});

const getChartDomain = (
  chartData: AssessmentChartLabelToValueMap[],
  highlightZones: AssessmentChartHighlightZone[],
  timelineMarkers: AssessmentChartTimelineMarker[],
  xAxisDomain?: [number, number],
): [number, number] | null => {
  if (xAxisDomain) return xAxisDomain;

  const values = [
    ...chartData.map((record) => record.epochCompletedOrUpdatedDate),
    ...highlightZones.flatMap((zone) => [zone.startEpoch, zone.endEpoch]),
    ...timelineMarkers.map((marker) => marker.epoch),
  ].filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    const oneDayMs = 24 * 60 * 60 * 1000;
    return [min - oneDayMs, max + oneDayMs];
  }

  return [min, max];
};

const findLastCompletedRecord = (
  records: AssessmentChartLabelToValueMap[],
): AssessmentChartLabelToValueMap | null => {
  for (let i = records.length - 1; i >= 0; i--) {
    if (records[i].status === AssessmentDisplayStatus.COMPLETE) {
      return records[i];
    }
  }
  return null;
};

const getChartLineKeys = (
  records: AssessmentChartLabelToValueMap[],
): string[] => {
  const keys = new Set<string>();

  records
    .filter((record) => record.status === AssessmentDisplayStatus.COMPLETE)
    .forEach((record) => {
      Object.keys(record)
        .filter((key) => !(AssessmentUnchartedKeys as string[]).includes(key))
        .forEach((key) => keys.add(key));
    });

  return Array.from(keys);
};

type TooltipDwellTrackerProps = React.ComponentProps<
  typeof AssessmentChartTooltip
> & {
  onDwellEnd?: (dwellMs: number) => void;
};

const TooltipDwellTracker = ({
  onDwellEnd,
  ...tooltipProps
}: TooltipDwellTrackerProps) => {
  const { active } = tooltipProps;
  const startedAtRef = useRef<number | null>(null);
  const onDwellEndRef = useRef(onDwellEnd);
  onDwellEndRef.current = onDwellEnd;

  useEffect(() => {
    if (active) {
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
      }
      return;
    }
    if (startedAtRef.current !== null) {
      onDwellEndRef.current?.(Date.now() - startedAtRef.current);
      startedAtRef.current = null;
    }
  }, [active]);

  useEffect(
    () => () => {
      if (startedAtRef.current !== null) {
        onDwellEndRef.current?.(Date.now() - startedAtRef.current);
        startedAtRef.current = null;
      }
    },
    [],
  );

  return <AssessmentChartTooltip {...tooltipProps} />;
};

const AssessmentChartCard = (props: AssessmentChartCardProps) => {
  const {
    assessmentName,
    avatarTooltipWrapper,
    chartData,
    descriptionText,
    frequency,
    id,
    isAssessmentActive,
    isHistoryEnabled,
    hideCreateActions = false,
    onClickCreateNew,
    onCompleteTogetherClick,
    onDeleteClick,
    onLineClick,
    onLineHoverEnd,
    onTooltipDwellEnd,
    onResumeClick,
    onViewAssessmentClick,
    onViewHistoryClick,
    primaryScoreKey,
  } = props;
  const {
    chartHeight = 280,
    dashedMissingBridge = false,
    highlightZones = [],
    hoverFocusDim = false,
    monthlyXAxisTicks = false,
    padScoresToTwoDecimals = false,
    shadingLegendEntries,
    showHoverVerticalLine = true,
    showTodayMarker = false,
    solidHorizontalGrid = false,
    timepointZones = [],
    timelineMarkers = [],
    xAxisDomain,
    xAxisLabelFormat = 'default',
    yAxisMax,
    yAxisMin,
  } = props;
  const [
    neutralSecondary,
    neutralLine,
    neutralTertiary,
    todayMarkerColor,
    neutralPrimary,
    backgroundPrimary,
  ] = useColorTokens([
    'neutral.secondary',
    'neutral.line',
    'neutral.tertiary',
    'primary.50',
    'neutral.primary',
    'neutral.background.primary',
  ]);
  const timelineMarkerVariantToStyle = buildTimelineMarkerVariantToStyle(
    neutralSecondary,
    neutralPrimary,
  );
  const lineColors = useColorTokens(orderedLineColors);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const hoverStartedAtRef = useRef<number | null>(null);
  const hoveredKeyRef = useRef<string | null>(null);

  const setHoveredKeyAndTrack = useCallback(
    (next: string | null) => {
      const prev = hoveredKeyRef.current;
      if (next === prev) return;
      const now = Date.now();
      if (prev !== null && hoverStartedAtRef.current !== null) {
        onLineHoverEnd?.(prev, now - hoverStartedAtRef.current);
      }
      hoverStartedAtRef.current = next !== null ? now : null;
      hoveredKeyRef.current = next;
      setHoveredKey(next);
    },
    [onLineHoverEnd],
  );

  const mostRecentRecord =
    chartData.length > 0 ? chartData[chartData.length - 1] : undefined;
  const mostRecentCompletedRecord = findLastCompletedRecord(chartData);
  const sortedHighlightZones = [
    ...highlightZones,
    ...timepointZones.map(mapTimepointZoneToHighlightZone),
  ].sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0));
  const chartDomain = getChartDomain(
    chartData,
    sortedHighlightZones,
    timelineMarkers,
    xAxisDomain,
  );
  const shouldRenderChart = chartDomain !== null;
  const chartLineKeys = getChartLineKeys(chartData);
  const chartMinWidth = '100%';
  return (
    <Grid
      background="neutral.background.primary"
      borderColor="neutral.line"
      borderRadius={cornerRadius.large}
      borderWidth="1px"
      gridTemplateAreas={`"header" "score" "chart" "action-message"`}
      gridTemplateColumns="1fr"
      gridTemplateRows="min-content min-content 1fr min-content"
      id={`${idPrefix}-container__${id}`}
      padding="24px"
    >
      <GridItem area="header" marginBottom="12px">
        <AssessmentCardHeader
          assessmentName={assessmentName}
          descriptionText={descriptionText}
          frequency={frequency}
          isAssessmentActive={isAssessmentActive}
          isHistoryEnabled={isHistoryEnabled}
          hideCreateActions={hideCreateActions}
          mostRecentAssessmentResponseId={
            mostRecentRecord?.assessmentResponseId
          }
          mostRecentAssessmentStatus={mostRecentRecord?.status}
          onCreateNewClick={onClickCreateNew}
          onDeleteClick={onDeleteClick}
          onResumeClick={onResumeClick}
          onViewAssessmentClick={onViewAssessmentClick}
          onViewHistoryClick={onViewHistoryClick}
        />
      </GridItem>
      {primaryScoreKey &&
        mostRecentCompletedRecord?.[primaryScoreKey] !== undefined && (
          <GridItem
            alignSelf="start"
            area="score"
            data-testid="assessment-card-score"
            marginBottom="18px"
            width="100%"
          >
            <Flex alignItems="flex-end" flexWrap="wrap" gap="12px 36px">
              <Flex direction="column" gap="4px">
                <Text color="neutral.primary" variant="captionMedium">
                  {primaryScoreKey}
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Flex alignItems="flex-end" gap="4px">
                    <Text color="neutral.primary" variant="h5">
                      {(() => {
                        const score =
                          mostRecentCompletedRecord[primaryScoreKey];
                        return typeof score === 'number' &&
                          padScoresToTwoDecimals
                          ? score.toFixed(2)
                          : score;
                      })()}
                    </Text>
                    {yAxisMax !== undefined && (
                      <>
                        <Text color="neutral.secondary" variant="caption">
                          /
                        </Text>
                        <Text color="neutral.secondary" variant="caption">
                          {yAxisMax}
                        </Text>
                      </>
                    )}
                  </Flex>
                  {mostRecentCompletedRecord?.riskLevel && (
                    <AssessmentCardRiskLevelTag
                      id={id}
                      riskLevel={mostRecentCompletedRecord.riskLevel}
                    />
                  )}
                </Flex>
              </Flex>
              {mostRecentCompletedRecord?.completedOrUpdatedBy && (
                <Flex alignItems="center" gap="8px">
                  <Avatar
                    id={
                      mostRecentCompletedRecord.completedOrUpdatedByUserId ||
                      undefined
                    }
                    name={mostRecentCompletedRecord.completedOrUpdatedBy}
                    size="2xs"
                    src={
                      mostRecentCompletedRecord.completedOrUpdatedByUserAvatarSrc ||
                      undefined
                    }
                    tooltipWrapper={
                      mostRecentCompletedRecord?.completedOrUpdatedByUserType ===
                      'provider'
                        ? avatarTooltipWrapper
                        : undefined
                    }
                    userType={
                      mostRecentCompletedRecord.completedOrUpdatedByUserType ??
                      'patient'
                    }
                  />
                  <Text color="neutral.secondary" variant="caption">
                    {`${mostRecentCompletedRecord.completedOrUpdatedBy} on ${DateTime.fromMillis(
                      mostRecentCompletedRecord.epochCompletedOrUpdatedDate,
                    ).toFormat('EEEE, MMM d, yyyy')}`}
                  </Text>
                </Flex>
              )}
            </Flex>
          </GridItem>
        )}
      <GridItem area="chart">
        {shouldRenderChart && (
          <>
            <Box overflowX="auto" width="100%">
              <Box minWidth={chartMinWidth}>
                <ResponsiveContainer height={chartHeight} width="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 44, right: 20, bottom: 5, left: -25 }}
                  >
                    {sortedHighlightZones.map((zone) => {
                      const style = highlightVariantToStyle[zone.variant];
                      const subLabel =
                        zone.subLabel ?? style.labelSuffix?.trim();
                      const labelsInLegend =
                        zone.variant === 'assessment-current' ||
                        zone.variant === 'assessment-next' ||
                        zone.variant === 'assessment-overdue';
                      return (
                        <ReferenceArea
                          fill={style.fill}
                          fillOpacity={style.fillOpacity}
                          ifOverflow={xAxisDomain ? 'hidden' : 'extendDomain'}
                          key={`${zone.label}-${zone.startEpoch}-${zone.endEpoch}-${zone.variant}`}
                          label={
                            labelsInLegend ? undefined : (
                              <HighlightZoneLabel
                                label={zone.label}
                                subLabel={subLabel}
                                textColor={style.textColor}
                              />
                            )
                          }
                          stroke={style.stroke ?? style.fill}
                          strokeDasharray={style.strokeDasharray}
                          strokeOpacity={0.3}
                          x1={zone.startEpoch}
                          x2={zone.endEpoch}
                        />
                      );
                    })}
                    {solidHorizontalGrid ? (
                      <CartesianGrid stroke={neutralLine} vertical={false} />
                    ) : (
                      <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                    )}
                    <XAxis
                      dataKey="epochCompletedOrUpdatedDate"
                      domain={chartDomain}
                      scale="time"
                      stroke={neutralSecondary}
                      tick={{ fontSize: 13 }}
                      tickFormatter={(value: number, index: number) => {
                        const dt = DateTime.fromMillis(value);
                        if (xAxisLabelFormat === 'monthYear') {
                          const monthLabel = dt.toFormat('LLL');
                          return dt.month === 1 || index === 0
                            ? `${monthLabel} '${dt.toFormat('yy')}`
                            : monthLabel;
                        }
                        return dt.toFormat('LL/dd');
                      }}
                      ticks={
                        monthlyXAxisTicks
                          ? getMonthlyTicks(chartDomain[0], chartDomain[1])
                          : undefined
                      }
                      type="number"
                    />
                    <YAxis
                      allowDecimals={false}
                      domain={
                        yAxisMin !== undefined ||
                        yAxisMax !== undefined ||
                        chartData.length === 0
                          ? [yAxisMin ?? 0, yAxisMax ?? 1]
                          : undefined
                      }
                      stroke={neutralSecondary}
                      tick={{ fontSize: 13 }}
                    />
                    {showTodayMarker && (
                      <ReferenceLine
                        ifOverflow="extendDomain"
                        stroke={todayMarkerColor}
                        strokeWidth={1}
                        x={Date.now()}
                      />
                    )}
                    {timelineMarkers.map((marker) => {
                      const style =
                        timelineMarkerVariantToStyle[
                          marker.variant ?? 'week16'
                        ];

                      return (
                        <ReferenceLine
                          ifOverflow={xAxisDomain ? 'hidden' : 'extendDomain'}
                          key={`${marker.label}-${marker.epoch}-${
                            marker.variant ?? 'default'
                          }`}
                          label={
                            <TimelineMarkerLabel
                              fillColor={backgroundPrimary}
                              label={marker.label}
                              strokeColor={style.stroke}
                              strokeDasharray={style.strokeDasharray}
                              textColor={style.textColor}
                            />
                          }
                          stroke={style.stroke}
                          strokeDasharray={style.strokeDasharray}
                          strokeLinecap="round"
                          strokeWidth={1.5}
                          x={marker.epoch}
                        />
                      );
                    })}
                    <RechartsTooltip
                      content={(tooltipProps) => (
                        <TooltipDwellTracker
                          onDwellEnd={onTooltipDwellEnd}
                          padScoresToTwoDecimals={padScoresToTwoDecimals}
                          primaryScoreKey={primaryScoreKey}
                          {...tooltipProps}
                        />
                      )}
                      cursor={showHoverVerticalLine ? undefined : false}
                    />
                    {chartLineKeys.map((key, index) => {
                      const color = lineColors[index % lineColors.length];
                      const isEmphasized =
                        hoverFocusDim && key === primaryScoreKey;
                      const isFocused = hoverFocusDim && hoveredKey === key;
                      const isDimmed =
                        hoverFocusDim &&
                        hoveredKey !== null &&
                        hoveredKey !== key;
                      const lineStroke = isDimmed ? neutralTertiary : color;
                      const lineOpacity = isDimmed ? 0.5 : 1;
                      const baseWidth = isEmphasized ? 2.4 : 1.6;
                      const focusedWidth = isEmphasized ? 3 : 2.4;
                      let lineWidth = 2;
                      if (hoverFocusDim) {
                        lineWidth = isFocused ? focusedWidth : baseWidth;
                      }
                      const dotStroke = isDimmed ? neutralTertiary : color;
                      const dotStrokeOpacity = isDimmed ? 0.6 : 1;
                      const transitionStyle = {
                        transition:
                          'stroke 160ms ease, stroke-opacity 160ms ease, stroke-width 160ms ease',
                      };
                      return (
                        <Fragment key={key}>
                          {dashedMissingBridge && (
                            <Line
                              connectNulls
                              dataKey={key}
                              dot={false}
                              isAnimationActive={false}
                              stroke={lineStroke}
                              strokeDasharray="4 4"
                              strokeOpacity={
                                isDimmed ? lineOpacity * 0.8 : lineOpacity
                              }
                              strokeWidth={lineWidth}
                              style={transitionStyle}
                              type="linear"
                            />
                          )}
                          <Line
                            activeDot={{
                              r: 7,
                              fill: color,
                              stroke: color,
                              strokeWidth: 0,
                              onClick: (_, event) => {
                                // the TS type for this recharts arg seems inaccurate
                                // casting to what we observe
                                const payload = (
                                  event as unknown as {
                                    payload: AssessmentChartLabelToValueMap;
                                  }
                                ).payload;
                                if (onLineClick) {
                                  onLineClick(key, payload.assessmentResponseId);
                                } else {
                                  onViewAssessmentClick(
                                    payload.assessmentResponseId,
                                  );
                                }
                              },
                              cursor: 'pointer',
                            }}
                            connectNulls
                            dataKey={key}
                            dot={{
                              r: 5,
                              fill: dotStroke,
                              stroke: dotStroke,
                              strokeOpacity: dotStrokeOpacity,
                              strokeWidth: 0,
                            }}
                            isAnimationActive={false}
                            stroke={lineStroke}
                            strokeOpacity={lineOpacity}
                            strokeWidth={lineWidth}
                            style={transitionStyle}
                            type="linear"
                          />
                          {hoverFocusDim && (
                            <Line
                              activeDot={false}
                              dataKey={key}
                              dot={false}
                              isAnimationActive={false}
                              onMouseEnter={() => setHoveredKeyAndTrack(key)}
                              onMouseLeave={() => setHoveredKeyAndTrack(null)}
                              stroke="transparent"
                              strokeWidth={18}
                              style={{
                                pointerEvents: 'stroke',
                                cursor: 'pointer',
                              }}
                              type="linear"
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <AssessmentChartCardLegend
              hoveredLine={hoverFocusDim ? hoveredKey : null}
              onHoverLine={hoverFocusDim ? setHoveredKey : undefined}
              payload={chartLineKeys.map((key, index) => ({
                dataKey: key,
                color: lineColors[index % lineColors.length],
              }))}
              shadingEntries={[
                ...(shadingLegendEntries?.map(({ label, variant }) => {
                  const style = highlightVariantToStyle[variant];
                  return {
                    fill: style.fill,
                    fillOpacity: style.fillOpacity,
                    label,
                    stroke: style.stroke,
                  };
                }) ?? []),
                ...(showTodayMarker
                  ? [
                      {
                        fill: todayMarkerColor,
                        label: 'Today',
                        swatchHeight: '12px',
                        swatchWidth: '2px',
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}
      </GridItem>
      <GridItem area="action-message">
        {mostRecentRecord && (
          <AssessmentCardActionMessage
            assessmentResponseId={mostRecentRecord.assessmentResponseId}
            assignee={mostRecentRecord.assignee}
            epochCreatedDate={mostRecentRecord.epochCreatedDate}
            frequency={frequency}
            onCompleteTogetherClick={onCompleteTogetherClick}
            responseUpdatedBy={mostRecentRecord.completedOrUpdatedBy}
            status={mostRecentRecord.status}
          />
        )}
      </GridItem>
    </Grid>
  );
};

export default AssessmentChartCard;
